import { GridColDef } from "@mui/x-data-grid-pro";
import {
  DeleteIcon,
  GKButton,
  GKClientDataGrid,
  GKTextField,
  IconButton,
  Plus1Icon,
  Show,
} from "components";
import { OrderSimulationForm } from "interfaces/OrderSimulation";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { extraColumnsField, validationForPercentage } from "utils/helpers";
import CompanyModel from "./CompanyModel";

const CompanyEditTable = ({
  watchAllocationType,
  isRebalanceCustomSell,
  isRebalanceCustomBuy,
}: {
  watchAllocationType: string;
  isRebalanceCustomSell?: boolean;
  isRebalanceCustomBuy?: boolean;
}) => {
  const form = useFormContext<OrderSimulationForm>();

  const [openCompanyModel, setOpenCompanyModel] = useState(false);

  const columns: (GridColDef & { visibleType?: string[] })[] = [
    {
      ...extraColumnsField(
        `Company name (${isRebalanceCustomSell ? form.getValues("allocation.sellCompanies").length || 0 : isRebalanceCustomBuy ? form.getValues("allocation.buyCompanies").length || 0 : form.getValues("allocation.companies")?.length || 0})`,
        "name",
        300,
        1,
      ),
      sortable: false,
      visibleType: ["allocation", "exit", "rebalance"],
    },
    {
      ...extraColumnsField("Weightage (%)", "weightage", 150, 1),
      sortable: false,
      visibleType: ["allocation", "exit", "rebalance"],
      renderCell: (params) => {
        const { index } = params?.row;

        return (
          <Controller
            control={form.control}
            rules={{
              max: 100,
            }}
            name={
              isRebalanceCustomSell
                ? `allocation.sellCompanies.${index}.weightage`
                : isRebalanceCustomBuy
                  ? `allocation.buyCompanies.${index}.weightage`
                  : `allocation.companies.${index}.weightage`
            }
            render={({ field }) => (
              <GKTextField
                {...field}
                size="small"
                type="number"
                placeholder="Weightage"
                onChange={(e) => {
                  const { value } = e.target;
                  field.onChange(
                    value === ""
                      ? ""
                      : validationForPercentage(+e.target.value),
                  );
                }}
              />
            )}
          />
        );
      },
    },
    {
      ...extraColumnsField("Limit value (₹)", "limitValue", 150, 1),
      sortable: false,
      visibleType: ["allocation"],
      renderCell: (params: { row: { index: any } }) => {
        const { index } = params?.row;
        return (
          <Controller
            control={form.control}
            name={`allocation.companies.${index}.limitValue`}
            render={({ field }) => (
              <GKTextField
                {...field}
                size="small"
                type="number"
                placeholder="Limit value"
                value={field.value === 0 ? "" : field.value}
                onChange={(e) => {
                  const { value } = e.target;
                  field.onChange(value === "" ? "" : Number(value));
                }}
              />
            )}
          />
        );
      },
    },
    {
      ...extraColumnsField("Actions", "action", 100, 1),
      align: "right",
      headerAlign: "right",
      visibleType: ["allocation", "exit", "rebalance"],
      renderCell: (params) => (
        <IconButton
          onClick={() => {
            if (isRebalanceCustomSell) {
              form.setValue(
                "allocation.sellCompanies",
                form
                  .getValues("allocation.sellCompanies")
                  .filter(
                    (dataItem: { gridCode: string }) =>
                      dataItem.gridCode !== params.row.gridCode,
                  ),
              );
            } else if (isRebalanceCustomBuy) {
              form.setValue(
                "allocation.buyCompanies",
                form
                  .getValues("allocation.buyCompanies")
                  .filter(
                    (dataItem: { gridCode: string }) =>
                      dataItem.gridCode !== params.row.gridCode,
                  ),
              );
            } else {
              form.setValue(
                "allocation.companies",
                form
                  .getValues("allocation.companies")
                  .filter(
                    (dataItem: { gridCode: string }) =>
                      dataItem.gridCode !== params.row.gridCode,
                  ),
              );
            }
          }}
        >
          <DeleteIcon color=" #C35245" />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Controller
        control={form.control}
        name={
          isRebalanceCustomSell
            ? "allocation.sellCompanies"
            : isRebalanceCustomBuy
              ? "allocation.buyCompanies"
              : "allocation.companies"
        }
        render={({ field }) => (
          <GKClientDataGrid
            name="companyAllocation"
            getRowId={(op) => op?.gridCode}
            columns={columns.filter((column) => {
              return column.visibleType?.includes(watchAllocationType);
            })}
            rows={
              field?.value.map((da: any, i: number) => {
                return {
                  ...da,
                  index: i,
                  limitValue:
                    da.limitValue === 0 || da.limitValue === undefined
                      ? ""
                      : da.limitValue,
                };
              }) || []
            }
            headerComponent={
              <GKButton
                startIcon={<Plus1Icon />}
                onClick={() => setOpenCompanyModel(true)}
              >
                Add New
              </GKButton>
            }
            showSearch={false}
            columnsButton={false}
            maxHeight={`calc(100vh - 335px)`}
          />
        )}
      />

      <Show.When isTrue={openCompanyModel}>
        <CompanyModel
          open={openCompanyModel}
          setOpen={setOpenCompanyModel}
          modelTitle={
            isRebalanceCustomSell
              ? "Select Sell Companies"
              : isRebalanceCustomBuy
                ? "Select Buy Companies"
                : "Select Companies"
          }
          selectedCompanies={
            isRebalanceCustomSell
              ? form.getValues("allocation.sellCompanies")
              : isRebalanceCustomBuy
                ? form.getValues("allocation.buyCompanies")
                : form.getValues("allocation.companies")
          }
          onSuccess={(companyList) => {
            if (isRebalanceCustomSell) {
              form.setValue("allocation.sellCompanies", companyList);
              form.setValue("extraThings.rightVisibleTable", "SELL_COMPANY");
            } else if (isRebalanceCustomBuy) {
              form.setValue("allocation.buyCompanies", companyList);
              form.setValue("extraThings.rightVisibleTable", "BUY_COMPANY");
            } else {
              form.setValue("allocation.companies", companyList);
              form.setValue("extraThings.rightVisibleTable", "EDIT_COMPANY");
            }
          }}
        />
      </Show.When>
    </>
  );
};

export default CompanyEditTable;
